"use client";

// Data layer VOUCHER sisi PEMBELI (mig 5610): kandidat voucher, pratinjau
// otoritatif, dan checkout ber-voucher. Semuanya RPC SECURITY DEFINER yang
// menurunkan toko & pelanggan dari sesi login (`auth.uid()` → customers) dan
// FAIL-CLOSED di server (tanpa toko/pelanggan → kosong / ditolak), jadi tak ada
// query tabel langsung di sini yang perlu difilter `store_id` oleh klien.
// Sesi default: di storefront = cookie pembeli; hanya pembeli login yang
// pernah mendapat kandidat (tamu → []).

import { createBrowserSupabase } from "@/lib/supabase/browser";
import {
  mapOngkirVoucherPreview,
  mapVoucherCandidateRows,
  mapVoucherCodeLookup,
  mapVoucherPreview,
  normalizeVoucherCode,
  ongkirVoucherGrossPayload,
  ongkirVoucherItemsPayload,
  voucherCheckoutLinePayload,
  voucherRpcLinePayload,
  type OngkirVoucherItem,
  type OngkirVoucherServerPreview,
  type VoucherCandidate,
  type VoucherCodeLookup,
  type VoucherKind,
  type VoucherRpcLine,
  type VoucherServerPreview
} from "@/lib/voucher";

// Tipe & mapper voucher ongkir kini hidup di `src/lib/voucher.ts` (dipakai juga
// jalur admin atas nama reseller, mig 5650); nama lama tetap diekspor dari sini.
export type { OngkirVoucherItem, OngkirVoucherServerPreview };

/**
 * Voucher aktif dalam periode milik toko pembeli + hitungan pemakaian + cakupan
 * produk yang DIJAWAB DB (keranjang klien tak memegang merek/kategori).
 * Best-effort: kegagalan apa pun (termasuk RPC belum ada) → [] — blok voucher
 * cukup tidak tampil, checkout biasa tak terganggu.
 */
/**
 * Kandidat voucher pembeli. Bawaannya galat → `[]` (pemanggil lama cukup
 * menampilkan "belum ada"). `throwOnError` untuk pemanggil yang WAJIB
 * membedakan "gagal dimuat" dari "kosong" — mis. halaman voucher keranjang,
 * yang dengan daftar kosong palsu mencari ulang voucher terpasang lewat kode
 * lamanya (bughunt D11-8).
 */
export async function fetchMyVoucherCandidates(
  kind: VoucherKind,
  productIds: readonly string[] = [],
  expedition: string | null = null,
  opts: { throwOnError?: boolean } = {}
): Promise<VoucherCandidate[]> {
  try {
    const db = createBrowserSupabase();
    if (!db) throw new Error("Supabase belum siap.");
    const { data, error } = await db.rpc("list_my_voucher_candidates", {
      p_kind: kind,
      p_product_ids: [...new Set(productIds.filter(Boolean))],
      p_expedition: expedition && expedition.trim() ? expedition.trim() : null
    });
    if (error) throw new Error(error.message);
    return mapVoucherCandidateRows(data);
  } catch (err) {
    if (opts.throwOnError) throw err;
    return [];
  }
}

// ─── Kode voucher (mig 6990) ────────────────────────────────────────────────

/**
 * Cari voucher ber-kode untuk keranjang / paket ini. Tidak menyimpan apa pun ke
 * akun (keputusan pemilik: kode berlaku untuk checkout SAAT ITU) — pemanggil
 * membawa kodenya sendiri ke pratinjau & checkout. Percobaan salah dicatat
 * server (batas 10 / 15 menit).
 */
export async function findMyVoucherByCode(
  code: string,
  kind: VoucherKind,
  productIds: readonly string[] = [],
  expedition: string | null = null
): Promise<VoucherCodeLookup> {
  try {
    const db = createBrowserSupabase();
    if (!db) return mapVoucherCodeLookup({ ok: false, reason: "error" });
    const { data, error } = await db.rpc("find_my_voucher_by_code", {
      p_code: normalizeVoucherCode(code) ?? "",
      p_kind: kind,
      p_product_ids: [...new Set(productIds.filter(Boolean))],
      p_expedition: expedition && expedition.trim() ? expedition.trim() : null
    });
    if (error) return mapVoucherCodeLookup({ ok: false, reason: "error" });
    return mapVoucherCodeLookup(data);
  } catch {
    return mapVoucherCodeLookup({ ok: false, reason: "error" });
  }
}

/**
 * Jenis voucher ber-kode yang sedang hidup di toko ini — menentukan apakah
 * kolom "Kode voucher" ditampilkan. Best-effort: gagal → [] (kolom tak tampil,
 * keranjang sama persis seperti sebelum mig 6990).
 */
export async function fetchMyVoucherCodeKinds(): Promise<VoucherKind[]> {
  try {
    const db = createBrowserSupabase();
    if (!db) return [];
    const { data, error } = await db.rpc("my_voucher_code_kinds");
    if (error || !Array.isArray(data)) return [];
    return data.filter((k): k is VoucherKind => k === "product" || k === "ongkir");
  } catch {
    return [];
  }
}

/**
 * Angka pratinjau OTORITATIF (server menghitung dari harga DB) untuk keranjang
 * yang sedang dilihat — dipakai blok Voucher sebelum tombol "Keep Produk".
 * `code` wajib untuk voucher ber-kode (mig 6990); tanpanya server menjawab
 * `reason: "code"`.
 */
export async function previewMyVoucher(
  voucherId: string,
  lines: readonly VoucherRpcLine[],
  code: string | null = null
): Promise<VoucherServerPreview> {
  const db = createBrowserSupabase();
  if (!db) return { ok: false, reason: "error", message: "Supabase belum siap." };
  const { data, error } = await db.rpc("preview_my_voucher", {
    p_voucher_id: voucherId,
    p_lines: lines.map(voucherRpcLinePayload),
    p_code: normalizeVoucherCode(code)
  });
  if (error) return { ok: false, reason: "error", message: error.message };
  return mapVoucherPreview(data);
}

export type VoucherCheckoutCart = {
  productId: string;
  lines: readonly VoucherRpcLine[];
};

export type VoucherCheckoutResult = {
  orders: Array<{ productId: string; orderId: string; orderCode: string; merged: boolean }>;
  usageId: string | null;
  voucherName: string;
  amountGranted: number;
  base: number;
};

/**
 * Checkout SEMUA cart dalam satu transaksi ber-voucher (RPC
 * `checkout_orders_with_voucher`). Melempar Error dengan pesan RPC apa adanya
 * (`VOUCHER_INVALID:<sebab>` dipetakan pemanggil lewat `voucherErrorMessage`).
 * `requestId` membuat pengiriman ulang idempoten (replay = hasil yang sama).
 */
export async function checkoutOrdersWithVoucher(args: {
  carts: readonly VoucherCheckoutCart[];
  voucherId: string;
  /** Kode voucher ber-kode (mig 6990) — ikut setiap checkout, tak disimpan ke akun. */
  code?: string | null;
  note?: string | null;
  address?: string | null;
  requestId: string;
}): Promise<VoucherCheckoutResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("checkout_orders_with_voucher", {
    p_carts: args.carts.map((c) => ({
      product_id: c.productId,
      // mig 8080 (bughunt D15 F1): baris tak layak voucher memotret tambahan
      // yang termasuk di harganya — pasangan yang sama dengan checkout tanpa voucher.
      lines: c.lines.map(voucherCheckoutLinePayload)
    })),
    p_voucher_id: args.voucherId,
    p_note: args.note ?? null,
    // Teks `orders.address` (bukan objek alamat pengiriman) — nama parameter
    // RPC-nya `p_order_address` supaya tak tertukar dengan `p_address` paket.
    p_order_address: args.address ?? null,
    p_request_id: args.requestId,
    p_code: normalizeVoucherCode(args.code)
  });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  const orders = Array.isArray(r.orders)
    ? (r.orders as Array<Record<string, unknown>>).map((o) => ({
        productId: String(o.productId ?? ""),
        orderId: String(o.orderId ?? ""),
        orderCode: String(o.orderCode ?? ""),
        merged: o.merged === true
      }))
    : [];
  return {
    orders,
    usageId: typeof r.usageId === "string" ? r.usageId : null,
    voucherName: typeof r.voucherName === "string" ? r.voucherName : "",
    amountGranted: Number(r.amountGranted ?? 0) || 0,
    base: Number(r.base ?? 0) || 0
  };
}

// ─── Voucher ONGKIR (mig 5620) ───────────────────────────────────────────────

/**
 * Kelayakan voucher ongkir dijawab SERVER (cakupan ekspedisi/tipe/minimum atas
 * isi paket sungguhan); `gross` = kutipan tarif bila ada → amount/net, null =
 * "dipotong saat admin menetapkan ongkir".
 */
export async function previewMyOngkirVoucher(
  voucherId: string,
  items: readonly OngkirVoucherItem[],
  expedition: string | null,
  gross: number | null,
  code: string | null = null
): Promise<OngkirVoucherServerPreview> {
  const db = createBrowserSupabase();
  if (!db) return { ok: false, reason: "error", message: "Supabase belum siap." };
  const { data, error } = await db.rpc("preview_my_ongkir_voucher", {
    p_voucher_id: voucherId,
    p_items: ongkirVoucherItemsPayload(items),
    p_expedition: expedition && expedition.trim() ? expedition.trim() : null,
    p_gross: ongkirVoucherGrossPayload(gross),
    p_code: normalizeVoucherCode(code)
  });
  if (error) return { ok: false, reason: "error", message: error.message };
  return mapOngkirVoucherPreview(data);
}
